import {
  DepartmentMenu,
  Role,
  roleAccessMap,
} from '@home/src/types/role-access.constants';

export interface MenuItem {
  icon: string;
  label: string;
  children?: any;
  childrenOpen?: boolean;
  url?: string;
  external: boolean;
  isPopup: boolean;
  tooltip?: string; // Optional tooltip for menu items
  roles?: string[]; // optional roles access only for higher order menu items.
}

const STORAGE_KEY = 'fincomData';

const sessionUser: any = (() => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
})();

function getDynamicResourceChildren(): any[] {
  const userRole = sessionUser?.positionCode;
  const additionalLinks = sessionUser?.additionalLinks;
  let links: any[] = [];

  // 1. Handle Additional Links if they exist and contain items
  if (Array.isArray(additionalLinks) && additionalLinks.length > 0) {
    links = additionalLinks.map((link: any) => ({
      label: link.name,
      url: link.url,
      external: true,
      isPopup: false,
    }));
  } else {
    // 2. Otherwise, fall back to Default Links
    links = [
      {
        label: 'NADA Analytics',
        url: 'https://www.nada.org/vistadashlogin',
        external: true,
        isPopup: false,
      },
      {
        label: 'Slide Guide',
        url: 'https://slideguide.nada.org/',
        external: true,
        isPopup: false,
      },
      {
        label: '20 Group Live',
        url: 'https://www.nada.org/20-group-live/login',
        external: true,
        isPopup: false,
      },
      {
        label: 'Dealership Workforce Study',
        url: 'https://www.nada.org/nada/20-group/nada-dealership-workforce-study',
        external: true,
        isPopup: false,
      },
      {
        label: 'NADA Education',
        url: 'https://www.nada.org/nada/education-consulting',
        external: true,
        isPopup: false,
      },
    ];
  }

  // 3. Inject Custom Report for STAFF (Applies to both additional and default paths)
  if (userRole === 'STAFF' || userRole === 'DLR' || userRole === 'CONSULT') {
    links.push({
      label: 'Generate Reports',
      url: '/Generate-Report',
      external: false,
      isPopup: false,
      tooltip: 'Generate Reports',
    });
  }
  return links;
}

function getDepartmentChildrenByRole(): MenuItem[] {
  // Uncomment the following lines if you want to use hardcoded Role for testing
  // const userRole = 'STAFF'
  const userRole = sessionUser?.positionCode as Role;
  const accessMap = roleAccessMap[userRole];
  if (!accessMap) return [];

  const menuItems: MenuItem[] = [
    {
      label: 'All Expenses',
      url: '/Department-Analysis/All-Expenses',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Total Dealership',
      url: '/Department-Analysis/Total-Dealership',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Balance Sheet',
      url: '/Department-Analysis/Balance-Sheet',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Variable Ops',
      url: '/Department-Analysis/Variable-Ops',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'New Vehicle',
      url: '/Department-Analysis/New-Vehicle',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Used Vehicle',
      url: '/Department-Analysis/Used-Vehicle',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'F&I',
      url: '/Department-Analysis/F&I',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Fixed Ops',
      url: '/Department-Analysis/Fixed-Ops',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Service',
      url: '/Department-Analysis/Service',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Parts & Accessories',
      url: '/Department-Analysis/Parts-&-Accessories',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Body Shop',
      url: '/Department-Analysis/Body-Shop',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Employee Productivity',
      url: '/Department-Analysis/Employee-Productivity',
      icon: '',
      external: false,
      isPopup: false,
    },
    {
      label: 'Collision Center',
      url: '/Department-Analysis/Collision-Center',
      icon: '',
      external: false,
      isPopup: false,
    },
  ];

  return menuItems.filter((item) => accessMap[item.label as DepartmentMenu]);
}

export const MenuItems: MenuItem[] = [
  {
    icon: '/assets/images/fin-com-act.svg',
    childrenOpen: false,
    label: 'FINANCIAL COMPOSITE',
    tooltip: 'Financial Composite',
    external: false,
    isPopup: false,
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
    children: [
      { label: 'Composite', url: '/Financial-Composite/Composite' },
      { label: 'KPI', url: '/Financial-Composite/KPI' },
      { label: 'Objectives', url: '/Financial-Composite/Objectives' },
      { label: 'Vitals', url: '/Financial-Composite/Vitals' },
    ],
  },
  {
    icon: '/assets/images/dep-ana-inact.svg',
    childrenOpen: false,
    label: 'DEPARTMENT ANALYSIS',
    tooltip: 'Department Analysis',
    external: false,
    isPopup: false,
    children: getDepartmentChildrenByRole(),
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/rep-dow-inact.svg',
    childrenOpen: false,
    label: 'REPORT DOWNLOAD',
    tooltip: 'Report Download',
    external: false,
    isPopup: false,
    url: '/Report-Download',
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/key-gra-inact.svg',
    childrenOpen: false,
    url: '/Key-Graph',
    label: 'KEY GRAPH',
    tooltip: 'Key Graph',
    external: false,
    isPopup: false,
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/part-inact.svg',
    childrenOpen: false,
    url: '/Participation',
    label: 'PARTICIPATION',
    tooltip: 'Participation',
    external: false,
    isPopup: false,
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/group-ros-inact.svg',
    childrenOpen: false,
    url: '/Group-Roster',
    label: 'GROUP ROSTER',
    tooltip: 'Group Roster',
    external: false,
    isPopup: false,
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/res-tool-inact.svg',
    childrenOpen: false,
    label: 'RESOURCES AND TOOLS',
    tooltip: 'Resources and Tools',
    external: false,
    isPopup: false,
    children: getDynamicResourceChildren(),
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'OFM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/inp-sheet-inact.svg',
    childrenOpen: false,
    url: '/Input-Sheet',
    label: 'INPUT SHEET',
    tooltip: 'Input Sheet',
    external: false,
    isPopup: false,
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'OFM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/sta-upl-inact.svg',
    childrenOpen: false,
    url: '/Statement-Upload',
    label: 'STATEMENT UPLOAD',
    tooltip: 'Statement Upload',
    external: false,
    isPopup: false,
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'OFM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/email-inact.svg',
    childrenOpen: false,
    url: 'mailto:',
    label: 'EMAIL CONSULTANT',
    tooltip: 'Email Consultant',
    external: true,
    isPopup: false,
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'OFM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/analyst.svg',
    childrenOpen: false,
    url: '/My-Analyst',
    label: 'MY ANALYST',
    tooltip: 'Analyst Profile',
    external: false,
    isPopup: false,
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'OFM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/help.svg',
    childrenOpen: false,
    url: '/Help',
    label: 'HELP',
    tooltip: 'Help ?',
    external: false,
    isPopup: false,
    roles: [
      'PSM',
      'FOM',
      'DGM',
      'SAM',
      'UVM',
      'GNM',
      'DLR',
      'SRM',
      'STAFF',
      'BDM',
      'OFM',
      'NVM',
      'VCP',
      'CONSULT',
    ],
  },
  {
    icon: '/assets/images/print-request.svg',
    childrenOpen: false,
    url: '/Print-Request',
    label: 'PRINT REQUEST',
    tooltip: 'Print Request',
    external: false,
    isPopup: false,
    roles: ['STAFF', 'CONSULT'],
  },
];
